<template>
  <div class="system-config-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp" v-loading="loading">
      <div v-if="error" class="error">{{ error }}</div>

      <el-collapse v-else v-model="activeSections" class="config-collapse">
        <el-collapse-item
          v-for="section in sections"
          :key="section.id"
          :name="section.id"
        >
          <template slot="title">
            <span class="section-title">
              {{ section.index }}、{{ section.title }}
              <span class="section-desc">（{{ section.desc }}）</span>
            </span>
          </template>

          <el-form
            :model="forms[section.id]"
            label-width="200px"
            class="section-form"
            @submit.native.prevent
          >
            <el-form-item
              v-for="field in visibleFields(section)"
              :key="field.key"
              :label="field.label"
              :required="field.required !== false"
            >
              <el-radio-group
                v-if="field.type === 'boolean'"
                v-model="forms[section.id][field.key]"
              >
                <el-radio label="true">开启</el-radio>
                <el-radio label="false">关闭</el-radio>
              </el-radio-group>
              <el-input
                v-else-if="field.type === 'textarea'"
                type="textarea"
                :rows="4"
                v-model="forms[section.id][field.key]"
                :placeholder="field.placeholder || '请输入' + field.label"
                maxlength="2000"
                show-word-limit
              />
              <div v-else-if="field.type === 'day_array'" class="day-array-wrap">
                <div class="day-array-tip">示例：[10,20,30,40,50,60,70]，按连续签到第 1～7 天填写</div>
                <div
                  v-for="(n, idx) in dayArrayValues"
                  :key="'day-' + idx"
                  class="day-array-row"
                >
                  <span class="day-label">第{{ idx + 1 }}天</span>
                  <el-input-number
                    v-model="dayArrayValues[idx]"
                    :min="0"
                    :step="1"
                    controls-position="right"
                    @change="syncDayArrayToForm(section.id, field.key)"
                  />
                </div>
              </div>
              <el-input-number
                v-else-if="field.type === 'number'"
                v-model="forms[section.id][field.key]"
                :min="field.min != null ? field.min : 0"
                :step="field.step || 1"
                controls-position="right"
                style="width: 220px"
              />
              <el-input
                v-else
                v-model.trim="forms[section.id][field.key]"
                clearable
                :placeholder="field.placeholder || '请输入' + field.label"
                style="max-width: 480px"
              />
              <div v-if="field.tip" class="field-tip">{{ field.tip }}</div>
            </el-form-item>

            <el-form-item>
              <el-button
                type="primary"
                :loading="savingSection === section.id"
                @click="handleSaveSection(section)"
              >
                保存修改
              </el-button>
            </el-form-item>
          </el-form>
        </el-collapse-item>
      </el-collapse>
    </el-card>
  </div>
</template>

<script>
import { get, post, put } from '@/utils/http.js'

const CHECKIN_DAILY_COINS_KEY = 'checkin_daily_coins'

/** 分组定义：与后台 system_config 的 config_key 一一对应，共 17 项 */
const SECTION_DEFS = [
  {
    id: 'adBasic',
    index: 1,
    title: '广告基础配置',
    desc: '激励视频、信息流、开屏、Banner 广告位',
    fields: [
      { key: 'reward_media_id', label: '激励视频广告位 ID', type: 'text' },
      { key: 'information_media_id', label: '信息流广告位 ID', type: 'text' },
      { key: 'splash_media_id', label: '开屏广告位 ID', type: 'text' },
      { key: 'banner_media_id', label: 'Banner 广告位 ID', type: 'text' }
    ]
  },
  {
    id: 'adAdvanced',
    index: 2,
    title: '广告高级配置',
    desc: '激励间隔、次数限制、收益占比、回调与叠包',
    fields: [
      {
        key: 'incentiveAdInterval',
        label: '激励广告间隔时间（秒）',
        type: 'number',
        min: 0
      },
      {
        key: 'Incentive_limit_times',
        label: '每日激励限制次数',
        type: 'number',
        min: 0,
        tip: '0 表示不限制'
      },
      {
        key: 'user_keep_rate',
        label: '激励视频用户收益占比',
        type: 'number',
        min: 0,
        tip: '百分比数值，如 60 表示用户获得 60%'
      },
      { key: 'callbackKey', label: '回调密钥', type: 'text' },
      { key: 'ad_pool_enabled', label: '叠包控制', type: 'boolean' },
      {
        key: 'ad_pool_min_reward_coins',
        label: '最低开包金币',
        type: 'number',
        min: 0,
        showWhen: { key: 'ad_pool_enabled', value: 'true' }
      }
    ]
  },
  {
    id: 'welfare',
    index: 3,
    title: '福利配置',
    desc: '红包连签与签到金币',
    fields: [
      {
        key: 'red_packet_streak_number',
        label: '每天需抢红包次数',
        type: 'number',
        min: 0
      },
      {
        key: 'coin_reward',
        label: '连续7天抢红包达标奖励金币',
        type: 'number',
        min: 0
      },
      {
        key: 'checkin_daily_coins',
        label: '七天连续签到每日金币',
        type: 'day_array'
      }
    ]
  },

  {
    id: 'notice',
    index: 5,
    title: '系统公告配置',
    desc: '公告开关与公告内容',
    fields: [
      { key: 'system_notice', label: '系统公告开关', type: 'boolean' },
      {
        key: 'tutorial',
        label: '系统公告内容',
        type: 'textarea',
        placeholder: '请输入系统公告内容',
        showWhen: { key: 'system_notice', value: 'true' }
      }
    ]
  }
]

const DEFAULT_DESCRIPTIONS = {
  reward_media_id: '激励视频广告位media_id',
  information_media_id: '信息流',
  splash_media_id: '开屏广告位media_id',
  banner_media_id: 'Banner广告位media_id',
  incentiveAdInterval: '激励广告间隔时间（秒）',
  Incentive_limit_times: '每日激励限制次数',
  user_keep_rate: '激励视频用户收益占比',
  callbackKey: '回调密钥',
  ad_pool_enabled: '叠包开关（提升收益激励）',
  ad_pool_min_reward_coins: '开包最低已叠金币',
  system_notice: '系统公告开关',
  tutorial: '系统公告内容',
  red_packet_streak_number: '每天需抢红包次数',
  coin_reward: '连续7天抢红包达标奖励金币',
  checkin_daily_coins: '七天连续签到每日金币',
  unlock_episodes: '每次广告解锁集数',
  free_episodes: '免广告观看的集数'
}

function emptyForms() {
  const forms = {}
  SECTION_DEFS.forEach(section => {
    const row = {}
    section.fields.forEach(field => {
      row[field.key] = null
    })
    forms[section.id] = row
  }) 
  return forms
}

function emptyDayArray() {
  return [null, null, null, null, null, null, null]
}

export default {
  name: 'SystemConfigManagement',
  data() {
    return {
      loading: false,
      savingSection: '',
      error: '',
      sections: SECTION_DEFS,
      activeSections: SECTION_DEFS.map(s => s.id),
      /** 原始记录：configKey -> { id, configKey, configValue, description } */
      configMap: {},
      forms: emptyForms(),
      dayArrayValues: emptyDayArray()
    }
  },
  mounted() {
    this.fetchConfigs()
  },
  methods: {
    visibleFields(section) {
      const form = this.forms[section.id] || {}
      return section.fields.filter(field => {
        if (!field.showWhen) return true
        return String(form[field.showWhen.key]) === String(field.showWhen.value)
      })
    },
    toBoolStr(value) {
      if (value == null || value === '') return null
      return String(value).trim().toLowerCase() === 'true' ? 'true' : 'false'
    },
    parseDayArray(value) {
      try {
        const arr = typeof value === 'string' ? JSON.parse(value) : value
        if (!Array.isArray(arr) || arr.length === 0) return null
        if (!arr.every(n => n !== null && n !== '' && !Number.isNaN(Number(n)))) return null
        return arr.map(n => Math.max(0, Math.floor(Number(n))))
      } catch (e) {
        return null
      }
    },
    syncDayArrayToForm(sectionId, fieldKey) {
      const arr = (this.dayArrayValues || []).slice(0, 7).map(n => {
        if (n === null || n === undefined || n === '') return null
        const v = Number(n)
        return Number.isFinite(v) ? Math.max(0, Math.floor(v)) : null
      })
      while (arr.length < 7) arr.push(null)
      this.dayArrayValues = arr
      if (arr.every(n => n === null)) {
        this.forms[sectionId][fieldKey] = null
      } else {
        this.forms[sectionId][fieldKey] = JSON.stringify(arr.map(n => (n == null ? 0 : n)))
      }
    },
    loadDayArrayFromValue(value) {
      const parsed = this.parseDayArray(value)
      if (!parsed) {
        this.dayArrayValues = emptyDayArray()
        this.forms.welfare[CHECKIN_DAILY_COINS_KEY] = null
        return
      }
      const arr = parsed.slice(0, 7)
      while (arr.length < 7) arr.push(null)
      this.dayArrayValues = arr
      this.forms.welfare[CHECKIN_DAILY_COINS_KEY] = JSON.stringify(arr.map(n => (n == null ? 0 : n)))
    },
    async fetchConfigs() {
      this.error = ''
      this.loading = true
      try {
        const result = await get('/admin/config/page', { page: 1, pageSize: 200 })
        const data = result.data || {}
        const records = Array.isArray(data.records) ? data.records : []
        const map = {}
        records.forEach(row => {
          if (row && row.configKey) map[row.configKey] = { ...row }
        })
        this.configMap = map
        this.applyConfigsToForms()
      } catch (e) {
        this.error = e && e.message ? e.message : '请求出现错误'
        this.configMap = {}
      } finally {
        this.loading = false
      }
    },
    applyConfigsToForms() {
      const forms = emptyForms()
      SECTION_DEFS.forEach(section => {
        section.fields.forEach(field => {
          const row = this.configMap[field.key]
          if (!row || row.configValue == null || row.configValue === '') return
          const raw = row.configValue
          if (field.type === 'boolean') {
            forms[section.id][field.key] = this.toBoolStr(raw)
          } else if (field.type === 'number') {
            const n = Number(raw)
            forms[section.id][field.key] = Number.isFinite(n) ? n : null
          } else if (field.type === 'day_array') {
            forms[section.id][field.key] = raw
          } else {
            forms[section.id][field.key] = String(raw)
          }
        })
      })
      this.forms = forms
      this.loadDayArrayFromValue(forms.welfare[CHECKIN_DAILY_COINS_KEY])
    },
    valueToPayload(field, formValue) {
      if (field.type === 'boolean') {
        if (formValue == null || formValue === '') return ''
        return this.toBoolStr(formValue) || ''
      }
      if (field.type === 'number') {
        if (formValue === '' || formValue === null || formValue === undefined) return ''
        return String(Math.floor(Number(formValue)))
      }
      if (field.type === 'day_array') {
        this.syncDayArrayToForm('welfare', field.key)
        return this.forms.welfare[field.key]
      }
      return formValue == null ? '' : String(formValue).trim()
    },
    async handleSaveSection(section) {
      if (section.id === 'welfare') {
        this.syncDayArrayToForm('welfare', CHECKIN_DAILY_COINS_KEY)
      }

      const form = this.forms[section.id]
      for (const field of this.visibleFields(section)) {
        const val = this.valueToPayload(field, form[field.key])
        if (field.type === 'day_array') {
          const parsed = this.parseDayArray(val)
          if (!parsed || parsed.length !== 7) {
            this.$message.error('请填写完整的 7 天签到金币')
            return
          }
        } else if (field.type === 'boolean') {
          if (val !== 'true' && val !== 'false') {
            this.$message.error(`请选择「${field.label}」`)
            return
          }
        } else if (val === '' || val == null) {
          this.$message.error(`请填写「${field.label}」`)
          return
        }
      }

      this.savingSection = section.id
      try {
        for (const field of this.visibleFields(section)) {
          const configValue = this.valueToPayload(field, form[field.key])
          const existing = this.configMap[field.key]
          const description =
            (existing && existing.description) ||
            DEFAULT_DESCRIPTIONS[field.key] ||
            field.label
          const payload = {
            configKey: field.key,
            configValue,
            description
          }
          if (existing && existing.id != null) {
            await put(`/admin/config/${existing.id}`, payload)
            this.configMap[field.key] = {
              ...existing,
              configValue,
              description
            }
          } else {
            const result = await post('/admin/config', payload)
            const created = (result && result.data) || {}
            this.configMap[field.key] = {
              id: created.id,
              configKey: field.key,
              configValue,
              description
            }
          }
        }
        this.$message.success('保存成功')
      } catch (e) {
        this.$message.error(e && e.message ? e.message : '保存失败')
      } finally {
        this.savingSection = ''
      }
    }
  }
}
</script>

<style scoped>
.system-config-management {
  min-height: 200px;
}

.error {
  color: #f87171;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.config-collapse {
  border: none;
}

.config-collapse >>> .el-collapse-item {
  margin-bottom: 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.config-collapse >>> .el-collapse-item__header {
  height: auto;
  min-height: 48px;
  line-height: 1.5;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #ebeef5;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.config-collapse >>> .el-collapse-item__wrap {
  border-bottom: none;
}

.config-collapse >>> .el-collapse-item__content {
  padding: 20px 16px 8px;
}

.section-title {
  display: inline-block;
  padding-right: 24px;
}

.section-desc {
  font-weight: 400;
  color: #909399;
  font-size: 13px;
}

.section-form {
  max-width: 720px;
}

.field-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}

.day-array-wrap {
  width: 100%;
}

.day-array-tip {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 10px;
}

.day-array-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.day-label {
  width: 56px;
  color: #606266;
  font-size: 13px;
}

.el-card.animate__animated {
  --animate-duration: 0.3s;
}
</style>
